import React, { useState, useEffect, useMemo, useRef } from "react";
import "./MealPlanDetailPage.css";
import EditLabel from "../../../components/EditLabel";
import {
    getMealPlan,
    updateMealPlan,
    deleteMealPlan,
    addPlannedMealPreferenceBatch,
    deletePlannedMealPreference,
    getCurrentMealPlan,
    setCurrentMealPlan,
    searchMealPlanPreferences,
    deleteMealPlanPreference,
    getMealPlanPreferenceNutrition,
    updateMealPlanPreference,
} from "../../../api/OtterApi";
import { useAuth0 } from "@auth0/auth0-react";
import CreatePlannedMealModal from "./CreatePlannedMealModal";
import ManageProductsModal from "./productmapping/ManageProductsModal";
import { useHistory } from "react-router-dom";
import MealCard from "./MealCard";
import * as FiIcons from "react-icons/fi";
import * as IoIcons from "react-icons/io";
import PreferenceSelector from "../../general/PreferenceSelector";
import NutritionSection from "./NutritionSection";
import BackupView from "../../../components/BackupView";
import { useNotificationContext } from "../../../contexts/NotificationContext";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
);

const MealPlanDetailPage = ({ match }) => {
    const mealplanId = match.params.id;
    const daysOfTheWeek = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];
    const history = useHistory();
    const { getAccessTokenSilently } = useAuth0();
    const [mealplan, setMealPlan] = useState();
    const [mealPlanPreferences, setMealPlanPreferences] = useState();
    const [currentMealplanId, setCurrentMealPlanId] = useState();
    const [description, setDescription] = useState();
    const [sortedPlannedMealPrefs, setSortedPlannedMealPrefs] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalPresets, setModalPresets] = useState(null);
    const [editingPlannedMealPrefId, setEditingPlannedMealPrefId] = useState(null);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [selectedPreference, setSelectedPreference] = useState();
    const [showNutrition, setShowNutrition] = useState(true);
    const [showDailyNutrition, setShowDailyNutrition] = useState(true);
    const [mobileDayIndex, setMobileDayIndex] = useState(0);
    const desktopHourRefs = useRef({});
    const mobileHourRefs = useRef({});
    const hasScrolledRef = useRef(false);
    const [nutrition, setNutrition] = useState();
    const [
        mealNutritionByPlannedMealPrefId,
        setMealNutritionByPlannedMealPrefId,
    ] = useState({});
    const [
        ingredientNutritionByPlannedMealPrefId,
        setIngredientNutritionByPlannedMealPrefId,
    ] = useState({});
    const [mealNutritionByLookupKey, setMealNutritionByLookupKey] = useState(
        {},
    );
    const [ingredientNutritionByLookupKey, setIngredientNutritionByLookupKey] =
        useState({});
    const [selectedChartDay, setSelectedChartDay] = useState("Monday");
    const [chartMode, setChartMode] = useState("discrete");
    const [chartTimeRange, setChartTimeRange] = useState([6, 22]);
    const [flow, setFlow] = useState("loading");
    const { addNotification } = useNotificationContext();

    const preferences =
        mealPlanPreferences?.sort((a, b) => {
            return a?.name > b?.name ? 1 : -1;
        }) || [];

    const onDeletePlannedMealPref = async (
        plannedMealPrefId,
        setPendingDeletion,
    ) => {
        const token = await getAccessTokenSilently();
        const response = await deletePlannedMealPreference(
            token,
            selectedPreference.id,
            plannedMealPrefId,
        );

        if (response.status === 200) {
            setSelectedPreference(response.data.mealplanpreference);

            instantiateMealPlanPreferences();
            addNotification({
                message: "Planned meal deleted successfully.",
            });
        } else {
            console.error(response);
            setFlow("error");
            addNotification({
                message: "Planned meal failed to delete.",
            });
        }

        setPendingDeletion(false);
    };

    const [dragOverCell, setDragOverCell] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [pendingMoves, setPendingMoves] = useState([]);

    // Touch drag state
    const touchDragRef = useRef(null);
    const touchTimerRef = useRef(null);
    const touchCloneRef = useRef(null);
    const touchMoveHandlerRef = useRef(null);
    const touchEndHandlerRef = useRef(null);
    const mobileScrollRef = useRef(null);
    const desktopScrollRef = useRef(null);
    const autoScrollIntervalRef = useRef(null);
    const hasInitializedMobileDayRef = useRef(false);

    const handleEdgeAutoScroll = (clientY, scrollEl) => {
        if (!scrollEl) return;
        const rect = scrollEl.getBoundingClientRect();
        const edgeZone = 48;
        const distFromTop = clientY - rect.top;
        const distFromBottom = rect.bottom - clientY;
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
        if (distFromTop < edgeZone && distFromTop >= 0) {
            const speed = Math.max(1, Math.round((1 - distFromTop / edgeZone) * 8));
            autoScrollIntervalRef.current = setInterval(() => {
                scrollEl.scrollTop -= speed;
            }, 16);
        } else if (distFromBottom < edgeZone && distFromBottom >= 0) {
            const speed = Math.max(1, Math.round((1 - distFromBottom / edgeZone) * 8));
            autoScrollIntervalRef.current = setInterval(() => {
                scrollEl.scrollTop += speed;
            }, 16);
        }
    };

    const stopAutoScroll = () => {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
    };

    const startTouchDrag = (plannedMealPref, touch, targetEl) => {
        const rect = targetEl.getBoundingClientRect();
        const clone = targetEl.cloneNode(true);
        clone.style.position = "fixed";
        clone.style.width = rect.width + "px";
        clone.style.left = touch.clientX - rect.width / 2 + "px";
        clone.style.top = touch.clientY - 20 + "px";
        clone.style.opacity = "0.85";
        clone.style.zIndex = "9999";
        clone.style.pointerEvents = "none";
        clone.style.boxShadow = "0 4px 16px rgba(0,0,0,0.18)";
        clone.style.borderRadius = "8px";
        clone.style.transform = "scale(1.04)";
        document.body.appendChild(clone);
        touchCloneRef.current = clone;
        // Dim the source card
        targetEl.style.opacity = "0.3";
        touchDragRef.current = {
            id: plannedMealPref.id,
            mealpreference_id: plannedMealPref.mealpreference_id,
            day: plannedMealPref.day,
            time: plannedMealPref.time,
            sourceEl: targetEl,
        };
        setIsDragging(true);
    };

    const handleTouchStart = (plannedMealPref, e) => {
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;
        const targetEl = e.currentTarget;
        touchTimerRef.current = setTimeout(() => {
            startTouchDrag(plannedMealPref, { clientX: startX, clientY: startY }, targetEl);
        }, 300);
        // Store start position to cancel on movement before long-press
        touchDragRef.current = { startX, startY, pending: true };
    };

    touchMoveHandlerRef.current = (e) => {
        if (touchDragRef.current?.pending && !touchCloneRef.current) {
            const touch = e.touches[0];
            const dx = Math.abs(touch.clientX - touchDragRef.current.startX);
            const dy = Math.abs(touch.clientY - touchDragRef.current.startY);
            if (dx > 10 || dy > 10) {
                clearTimeout(touchTimerRef.current);
                touchDragRef.current = null;
            }
            return;
        }
        if (!touchCloneRef.current) return;
        e.preventDefault();
        const touch = e.touches[0];
        touchCloneRef.current.style.left = touch.clientX - touchCloneRef.current.offsetWidth / 2 + "px";
        touchCloneRef.current.style.top = touch.clientY - 20 + "px";

        // Highlight drop target
        touchCloneRef.current.style.display = "none";
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        touchCloneRef.current.style.display = "";
        const cell = el?.closest("[data-drop-day][data-drop-slot]");
        if (cell) {
            setDragOverCell(`mobile-${cell.dataset.dropSlot}`);
        } else {
            setDragOverCell(null);
        }

        // Edge auto-scroll
        handleEdgeAutoScroll(touch.clientY, mobileScrollRef.current);
    };

    touchEndHandlerRef.current = (e) => {
        clearTimeout(touchTimerRef.current);
        stopAutoScroll();
        if (!touchCloneRef.current || !touchDragRef.current?.id) {
            touchDragRef.current = null;
            return;
        }
        const touch = e.changedTouches[0];
        touchCloneRef.current.style.display = "none";
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        touchCloneRef.current.style.display = "";

        // Clean up clone
        document.body.removeChild(touchCloneRef.current);
        touchCloneRef.current = null;
        setDragOverCell(null);
        setIsDragging(false);

        const dragData = touchDragRef.current;
        touchDragRef.current = null;
        // Restore source card opacity
        if (dragData.sourceEl) dragData.sourceEl.style.opacity = "";

        // Check if dropped on a specific meal card for swap
        const mealCardEl = el?.closest("[data-meal-pref-id]");
        const cell = el?.closest("[data-drop-day][data-drop-slot]");

        if (mealCardEl && cell) {
            const targetDay = cell.dataset.dropDay;
            const targetSlot = parseInt(cell.dataset.dropSlot, 10);
            const targetMealPrefId = mealCardEl.dataset.mealPrefId;
            const sameSlot =
                dragData.day.toLowerCase() === targetDay.toLowerCase() &&
                Math.floor(getMealMinutesFromTime(dragData.time) / 30) === targetSlot;

            if (sameSlot && targetMealPrefId !== String(dragData.id)) {
                // Find the target planned meal pref for swap
                const targetPref = sortedPlannedMealPrefs.find(
                    (p) => String(p.id) === targetMealPrefId
                );
                if (targetPref) {
                    onSwapPlannedMealPrefs(dragData, targetPref);
                    return;
                }
            }
        }

        if (cell) {
            const targetDay = cell.dataset.dropDay;
            const targetSlot = parseInt(cell.dataset.dropSlot, 10);
            onMovePlannedMealPref(dragData, targetDay, targetSlot);
        }
    };

    const onMovePlannedMealPref = async (dragData, targetDay, targetSlot) => {
        const dragMinutes = getMealMinutesFromTime(dragData.time);
        const dragSlot = dragMinutes != null ? Math.floor(dragMinutes / 30) : -1;
        if (
            dragData.day.toLowerCase() === targetDay.toLowerCase() &&
            dragSlot === targetSlot
        ) {
            return;
        }

        const moveId = `${Date.now()}-${Math.random()}`;
        setPendingMoves((prev) => [...prev, { id: moveId, day: targetDay, slot: targetSlot }]);

        const targetHour = Math.floor(targetSlot / 2);
        const targetMinute = (targetSlot % 2) * 30;
        const newTime = String(targetHour).padStart(2, "0") + String(targetMinute).padStart(2, "0");
        const token = await getAccessTokenSilently();

        const deleteResponse = await deletePlannedMealPreference(
            token,
            selectedPreference.id,
            dragData.id,
        );

        if (deleteResponse.status !== 200) {
            setPendingMoves((prev) => prev.filter((m) => m.id !== moveId));
            addNotification({ message: "Failed to move meal." });
            return;
        }

        const addResponse = await addPlannedMealPreferenceBatch(
            token,
            selectedPreference.id,
            [{
                mealpreference_id: dragData.mealpreference_id,
                day: targetDay,
                time: newTime,
            }],
        );

        setPendingMoves((prev) => prev.filter((m) => m.id !== moveId));

        if (addResponse.status === 200) {
            setSelectedPreference(addResponse.data.mealplanpreference);
            instantiateMealPlanPreferences();
            addNotification({ message: "Meal moved successfully." });
        } else {
            setSelectedPreference(deleteResponse.data.mealplanpreference);
            instantiateMealPlanPreferences();
            addNotification({ message: "Failed to move meal." });
        }
    };

    const onSwapPlannedMealPrefs = async (dragData, targetMealPref) => {
        if (dragData.id === targetMealPref.id) return;

        const token = await getAccessTokenSilently();

        const deleteA = await deletePlannedMealPreference(
            token,
            selectedPreference.id,
            dragData.id,
        );
        if (deleteA.status !== 200) {
            addNotification({ message: "Failed to reorder meal." });
            return;
        }

        const deleteB = await deletePlannedMealPreference(
            token,
            selectedPreference.id,
            targetMealPref.id,
        );
        if (deleteB.status !== 200) {
            addNotification({ message: "Failed to reorder meal." });
            setSelectedPreference(deleteA.data.mealplanpreference);
            instantiateMealPlanPreferences();
            return;
        }

        const addResponse = await addPlannedMealPreferenceBatch(
            token,
            selectedPreference.id,
            [
                {
                    mealpreference_id: targetMealPref.mealpreference_id,
                    day: targetMealPref.day,
                    time: targetMealPref.time,
                },
                {
                    mealpreference_id: dragData.mealpreference_id,
                    day: dragData.day,
                    time: dragData.time,
                },
            ],
        );

        if (addResponse.status === 200) {
            setSelectedPreference(addResponse.data.mealplanpreference);
            instantiateMealPlanPreferences();
            addNotification({ message: "Meals reordered successfully." });
        } else {
            setSelectedPreference(deleteB.data.mealplanpreference);
            instantiateMealPlanPreferences();
            addNotification({ message: "Failed to reorder meals." });
        }
    };

    useEffect(() => {
        if (mealPlanPreferences && !selectedPreference) {
            setSelectedPreference(mealPlanPreferences[0]);
        }
    }, [mealPlanPreferences, selectedPreference]);

    useEffect(() => {
        const initializeCurrentMealplan = async () => {
            const token = await getAccessTokenSilently();
            const response = await getCurrentMealPlan(token);
            if (response.status === 200) {
                setCurrentMealPlanId(response.data.mealplan_id);
            } else {
                console.error(response.data.error);
                setFlow("error");
            }
        };
        initializeCurrentMealplan();
    }, [getAccessTokenSilently]);

    const instantiateMealPlanPreferences = async () => {
        const token = await getAccessTokenSilently();
        const mealPlanPrefsResponse = await searchMealPlanPreferences(
            token,
            20,
            0,
            "",
            mealplanId,
        );
        if (mealPlanPrefsResponse.status === 200) {
            setMealPlanPreferences(
                mealPlanPrefsResponse.data.mealplanpreferences,
            );
        } else {
            console.error(mealPlanPrefsResponse);
            setFlow("error");
        }
    };

    useEffect(() => {
        const initializeMealPlan = async () => {
            const token = await getAccessTokenSilently();
            const response = await getMealPlan(token, mealplanId);
            if (response.status === 200) {
                setMealPlan(response.data.mealplan);
                setIsEditable(response.data.editable);

                instantiateMealPlanPreferences();
                setFlow("mealplan");
            } else {
                console.error(response);
                setFlow("error");
            }
        };
        initializeMealPlan();
    }, [getAccessTokenSilently, mealplanId]);

    useEffect(() => {
        if (selectedPreference) {
            setSortedPlannedMealPrefs(
                sortPlannedMealPrefs(
                    selectedPreference.plannedmeal_preferences,
                ),
            );

            const instantiateMealPlanPreferenceNutrition = async () => {
                const token = await getAccessTokenSilently();
                const response = await getMealPlanPreferenceNutrition(
                    token,
                    selectedPreference.id,
                );
                if (response.status === 200) {
                    const responseDays = response.data?.days;

                    if (responseDays && typeof responseDays === "object") {
                        const dailyNutrition = {};
                        const mealNutritionMap = {};
                        const ingredientNutritionMap = {};
                        const mealNutritionLookupMap = {};
                        const ingredientNutritionLookupMap = {};

                        Object.entries(responseDays).forEach(
                            ([dayKey, dayData]) => {
                                dailyNutrition[dayKey.toLowerCase()] =
                                    dayData?.nutrition;

                                (dayData?.meals || []).forEach((meal) => {
                                    const lookupKey = buildMealLookupKey({
                                        day: dayKey,
                                        mealPreferenceId:
                                            meal?.meal_preference_id,
                                        time: meal?.time,
                                    });

                                    if (lookupKey) {
                                        mealNutritionLookupMap[lookupKey] =
                                            meal?.nutrition;
                                        ingredientNutritionLookupMap[
                                            lookupKey
                                        ] = meal?.ingredients || [];
                                    }

                                    if (meal?.planned_meal_preference_id) {
                                        mealNutritionMap[
                                            meal.planned_meal_preference_id
                                        ] = meal?.nutrition;
                                        ingredientNutritionMap[
                                            meal.planned_meal_preference_id
                                        ] = meal?.ingredients || [];
                                    }
                                });
                            },
                        );

                        setNutrition(dailyNutrition);
                        setMealNutritionByPlannedMealPrefId(mealNutritionMap);
                        setIngredientNutritionByPlannedMealPrefId(
                            ingredientNutritionMap,
                        );
                        setMealNutritionByLookupKey(mealNutritionLookupMap);
                        setIngredientNutritionByLookupKey(
                            ingredientNutritionLookupMap,
                        );
                    } else {
                        // Backward compatibility with the previous response shape.
                        setNutrition(response.data?.nutrition);
                        setMealNutritionByLookupKey(
                            response.data?.meal_nutrition || {},
                        );
                        setMealNutritionByPlannedMealPrefId({});
                        setIngredientNutritionByPlannedMealPrefId({});
                        setIngredientNutritionByLookupKey({});
                    }
                } else {
                    console.error(
                        "Failed to get mealplan nutritional information",
                    );
                }
            };

            instantiateMealPlanPreferenceNutrition();
        }
    }, [selectedPreference, getAccessTokenSilently]);

    useEffect(() => {
        const dayWithMeals =
            daysOfTheWeek.find((day) =>
                sortedPlannedMealPrefs?.some(
                    (mealPref) =>
                        mealPref?.day?.toLowerCase() === day.toLowerCase(),
                ),
            ) || "Monday";

        setSelectedChartDay(dayWithMeals);
    }, [selectedPreference, sortedPlannedMealPrefs]);

    useEffect(() => {
        hasInitializedMobileDayRef.current = false;
    }, [selectedPreference?.id]);

    useEffect(() => {}, [description, isEditable]);

    // Global touch event listeners for mobile drag
    useEffect(() => {
        const onTouchMove = (e) => touchMoveHandlerRef.current?.(e);
        const onTouchEnd = (e) => touchEndHandlerRef.current?.(e);
        document.addEventListener("touchmove", onTouchMove, { passive: false });
        document.addEventListener("touchend", onTouchEnd);
        return () => {
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
            clearTimeout(touchTimerRef.current);
            stopAutoScroll();
            if (touchCloneRef.current) {
                document.body.removeChild(touchCloneRef.current);
                touchCloneRef.current = null;
            }
        };
    }, []);

    // Sort based on time
    const sortPlannedMealPrefs = (plannedMealPrefs) => {
        return plannedMealPrefs?.sort((a, b) =>
            a.time > b.time
                ? 1
                : a.time === b.time
                  ? a.time > b.time
                      ? 1
                      : -1
                  : -1,
        );
    };

    const normalizeMealTime = (time) => {
        if (!time && time !== 0) {
            return "";
        }

        const stringValue = String(time).trim();
        const digitsOnly = stringValue.replace(":", "");

        if (digitsOnly.length === 3) {
            return `0${digitsOnly}`;
        }

        return digitsOnly;
    };

    const getMealMinutesFromTime = (time) => {
        const normalizedTime = normalizeMealTime(time);

        if (!normalizedTime || normalizedTime.length < 4) {
            return null;
        }

        const hour = parseInt(normalizedTime.substring(0, 2), 10);
        const minute = parseInt(normalizedTime.substring(2, 4), 10);

        if (
            Number.isNaN(hour) ||
            Number.isNaN(minute) ||
            hour < 0 ||
            hour > 23 ||
            minute < 0 ||
            minute > 59
        ) {
            return null;
        }

        return hour * 60 + minute;
    };

    const firstMealSlot = useMemo(() => {
        let earliestMeal = null;

        sortedPlannedMealPrefs.forEach((plannedMealPref) => {
            const mealMinutes = getMealMinutesFromTime(plannedMealPref?.time);

            if (mealMinutes == null) {
                return;
            }

            if (!earliestMeal || mealMinutes < earliestMeal.mealMinutes) {
                earliestMeal = {
                    slot: Math.floor(mealMinutes / 30),
                    day: plannedMealPref?.day,
                    mealMinutes,
                };
            }
        });

        return earliestMeal;
    }, [sortedPlannedMealPrefs]);

    useEffect(() => {
        if (
            !firstMealSlot ||
            typeof window === "undefined" ||
            hasInitializedMobileDayRef.current
        ) {
            return;
        }

        const isMobile = window.matchMedia("(max-width: 900px)").matches;
        if (!isMobile) {
            return;
        }

        const firstMealDayIndex = daysOfTheWeek.findIndex(
            (day) => day.toLowerCase() === firstMealSlot?.day?.toLowerCase(),
        );

        if (firstMealDayIndex >= 0) {
            setMobileDayIndex(firstMealDayIndex);
        }

        hasInitializedMobileDayRef.current = true;
    }, [daysOfTheWeek, firstMealSlot]);

    useEffect(() => {
        if (!firstMealSlot || typeof window === "undefined" || hasScrolledRef.current) {
            return;
        }

        const isMobile = window.matchMedia("(max-width: 900px)").matches;
        const scrollContainer = isMobile
            ? mobileScrollRef.current
            : desktopScrollRef.current;
        const targetRefMap = isMobile
            ? mobileHourRefs.current
            : desktopHourRefs.current;
        const target = targetRefMap[firstMealSlot.slot];

        if (target && scrollContainer) {
            const pageScrollY = window.scrollY;
            target.scrollIntoView({ block: "start" });
            window.scrollTo(0, pageScrollY);
            hasScrolledRef.current = true;
        }
    }, [firstMealSlot, mobileDayIndex]);

    const getTimelineTimeLabel = (minutes) => {
        if (typeof minutes !== "number" || Number.isNaN(minutes)) {
            return "";
        }

        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const hhmm = `${String(hour).padStart(2, "0")}${String(minute).padStart(
            2,
            "0",
        )}`;

        return formatChartTimeLabel(hhmm);
    };

    const buildMealLookupKey = ({ day, mealPreferenceId, time }) => {
        if (!day || !mealPreferenceId) {
            return "";
        }

        return `${day.toLowerCase()}::${mealPreferenceId.toLowerCase()}::${normalizeMealTime(
            time,
        )}`;
    };

    const getDailyNutrition = (nutritionByDay, day) => {
        if (!nutritionByDay || !day) {
            return undefined;
        }

        const lowerDay = day.toLowerCase();
        const shortLowerDay = lowerDay.substring(0, 3);

        return (
            nutritionByDay[lowerDay] ||
            nutritionByDay[day] ||
            nutritionByDay[shortLowerDay] ||
            nutritionByDay[shortLowerDay.toUpperCase()] ||
            Object.entries(nutritionByDay).find(
                ([key]) => key.toLowerCase() === lowerDay,
            )?.[1]
        );
    };

    const formatChartTimeLabel = (time) => {
        if (!time && time !== 0) {
            return "Unknown";
        }

        const paddedTime = String(time).padStart(4, "0");
        const normalizedTime = String(time).includes(":")
            ? String(time)
            : `${paddedTime.substring(0, 2)}:${paddedTime.substring(2, 4)}`;

        const [rawHour, rawMinute = "00"] = normalizedTime.split(":");
        const hourAsInt = parseInt(rawHour, 10);

        if (Number.isNaN(hourAsInt)) {
            return normalizedTime;
        }

        if (hourAsInt === 0) {
            return `12:${rawMinute} AM`;
        }

        if (hourAsInt === 12) {
            return `12:${rawMinute} PM`;
        }

        if (hourAsInt > 12) {
            return `${hourAsInt - 12}:${rawMinute} PM`;
        }

        return `${hourAsInt}:${rawMinute} AM`;
    };

    const getChartMealsForDay = (day) => {
        const dayMeals =
            sortedPlannedMealPrefs?.filter(
                (plannedMealPref) =>
                    plannedMealPref?.day?.toLowerCase() === day?.toLowerCase(),
            ) || [];

        const mapped = dayMeals.map((plannedMealPref, index) => {
            const lookupKey = buildMealLookupKey({
                day,
                mealPreferenceId: plannedMealPref?.mealpreference_id,
                time: plannedMealPref?.time,
            });

            const mealNutrition =
                mealNutritionByPlannedMealPrefId[plannedMealPref?.id] ||
                mealNutritionByLookupKey[lookupKey] ||
                mealNutritionByLookupKey[plannedMealPref?.mealpreference_id];

            const sortKey =
                normalizeMealTime(plannedMealPref?.time) || `${index}`;
            const rawHour =
                sortKey.length >= 2
                    ? parseInt(sortKey.substring(0, 2), 10)
                    : null;
            return {
                sortKey,
                rawHour: Number.isNaN(rawHour) ? null : rawHour,
                timeLabel: formatChartTimeLabel(plannedMealPref?.time),
                nutrition: mealNutrition,
            };
        });

        // Merge entries with the same timeLabel by summing their nutrition values.
        const mergedMap = new Map();
        for (const meal of mapped) {
            if (mergedMap.has(meal.timeLabel)) {
                const existing = mergedMap.get(meal.timeLabel);
                const mergedNutrition = { ...existing.nutrition };
                if (meal.nutrition) {
                    for (const key of Object.keys(meal.nutrition)) {
                        const existingVal =
                            typeof mergedNutrition[key] === "number"
                                ? mergedNutrition[key]
                                : 0;
                        const newVal =
                            typeof meal.nutrition[key] === "number"
                                ? meal.nutrition[key]
                                : 0;
                        mergedNutrition[key] = existingVal + newVal;
                    }
                }
                mergedMap.set(meal.timeLabel, {
                    ...existing,
                    nutrition: mergedNutrition,
                });
            } else {
                mergedMap.set(meal.timeLabel, meal);
            }
        }

        return Array.from(mergedMap.values()).sort((a, b) =>
            a.sortKey.localeCompare(b.sortKey),
        );
    };

    const chartMealsForSelectedDay = useMemo(
        () => getChartMealsForDay(selectedChartDay),
        [
            selectedChartDay,
            sortedPlannedMealPrefs,
            mealNutritionByPlannedMealPrefId,
            mealNutritionByLookupKey,
        ],
    );

    const getMacroDatasets = (meals) => {
        const macros = [
            {
                key: "calories",
                label: "Calories",
                borderColor: "rgb(200, 200, 200)",
                backgroundColor: "rgba(200, 200, 200, 0.2)",
            },
            {
                key: "carbohydrates",
                label: "Carbs (g)",
                borderColor: "rgb(248, 214, 137)",
                backgroundColor: "rgba(248, 214, 137, 0.2)",
            },
            {
                key: "fats",
                label: "Fats (g)",
                borderColor: "rgb(136, 175, 248)",
                backgroundColor: "rgba(136, 175, 248, 0.2)",
            },
            {
                key: "proteins",
                label: "Proteins (g)",
                borderColor: "rgb(241, 161, 146)",
                backgroundColor: "rgba(241, 161, 146, 0.2)",
            },
        ];

        return macros.map((macro) => ({
            label: macro.label,
            data: meals.map((meal) => {
                const value = meal?.nutrition?.[macro.key];
                return typeof value === "number" && !Number.isNaN(value)
                    ? Number(value.toFixed(1))
                    : null;
            }),
            borderColor: macro.borderColor,
            backgroundColor: macro.backgroundColor,
            borderWidth: 2,
            tension: 0.3,
            spanGaps: true,
            pointRadius: 3,
            // Start with calories hidden and let users toggle from the legend.
            hidden: macro.key === "calories",
        }));
    };

    const formatHourLabel = (hour) => formatChartTimeLabel(hour * 100);

    const getFullDayAxis = () => {
        const [startHour, endHour] = chartTimeRange;
        const hours = [];
        for (let h = startHour; h <= endHour; h++) {
            hours.push(h);
        }
        return hours;
    };

    const getCumulativeMeals = (meals) => {
        const running = {};
        return meals.map((meal) => {
            const cumulativeNutrition = { ...running };
            if (meal.nutrition) {
                for (const key of Object.keys(meal.nutrition)) {
                    const prev =
                        typeof cumulativeNutrition[key] === "number"
                            ? cumulativeNutrition[key]
                            : 0;
                    const curr =
                        typeof meal.nutrition[key] === "number"
                            ? meal.nutrition[key]
                            : 0;
                    cumulativeNutrition[key] = Number((prev + curr).toFixed(1));
                    running[key] = cumulativeNutrition[key];
                }
            }
            return { ...meal, nutrition: cumulativeNutrition };
        });
    };

    const displayMeals =
        chartMode === "cumulative"
            ? getCumulativeMeals(chartMealsForSelectedDay)
            : chartMealsForSelectedDay;

    const fullDayHours = getFullDayAxis();
    const mealByHour = new Map(
        displayMeals
            .filter((m) => m.rawHour != null)
            .map((m) => [m.rawHour, m]),
    );

    const chartData = {
        labels: fullDayHours.map(formatHourLabel),
        datasets: getMacroDatasets(
            fullDayHours.map(
                (h) =>
                    mealByHour.get(h) ?? {
                        rawHour: h,
                        timeLabel: formatHourLabel(h),
                        nutrition: null,
                    },
            ),
        ),
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                title: {
                    display: true,
                    text: "Nutrition (g) / Calories",
                },
                beginAtZero: true,
            },
            x: {
                title: {
                    display: true,
                    text: "Meal Time",
                },
            },
        },
        plugins: {
            legend: {
                position: "bottom",
            },
            tooltip: {
                mode: "index",
                intersect: false,
            },
        },
    };

    const getMacroChart = () => {
        return (
            <div className="mealplan-detail-macro-chart-container">
                <div className="mealplan-detail-macro-chart-header">
                    <h4>Macronutrient Trend</h4>
                    <div className="mealplan-detail-macro-chart-selectors">
                        <div className="mealplan-detail-macro-chart-time-range">
                            <span className="mealplan-detail-macro-chart-time-range-label">
                                {formatHourLabel(chartTimeRange[0])}
                            </span>
                            <div className="mealplan-detail-macro-chart-slider">
                                <div
                                    className="mealplan-detail-macro-chart-slider-track-fill"
                                    style={{
                                        left: `${(chartTimeRange[0] / 23) * 100}%`,
                                        width: `${((chartTimeRange[1] - chartTimeRange[0]) / 23) * 100}%`,
                                    }}
                                />
                                <input
                                    type="range"
                                    min={0}
                                    max={23}
                                    value={chartTimeRange[0]}
                                    onChange={(e) => {
                                        const val = parseInt(
                                            e.target.value,
                                            10,
                                        );
                                        if (val < chartTimeRange[1]) {
                                            setChartTimeRange([
                                                val,
                                                chartTimeRange[1],
                                            ]);
                                        }
                                    }}
                                />
                                <input
                                    type="range"
                                    min={0}
                                    max={23}
                                    value={chartTimeRange[1]}
                                    onChange={(e) => {
                                        const val = parseInt(
                                            e.target.value,
                                            10,
                                        );
                                        if (val > chartTimeRange[0]) {
                                            setChartTimeRange([
                                                chartTimeRange[0],
                                                val,
                                            ]);
                                        }
                                    }}
                                />
                            </div>
                            <span className="mealplan-detail-macro-chart-time-range-label">
                                {formatHourLabel(chartTimeRange[1])}
                            </span>
                        </div>
                        <div className="mealplan-detail-macro-chart-day-selector">
                            <label htmlFor="macro-chart-day-selector">
                                Day
                            </label>
                            <select
                                id="macro-chart-day-selector"
                                value={selectedChartDay}
                                onChange={(event) =>
                                    setSelectedChartDay(event.target.value)
                                }
                            >
                                {daysOfTheWeek.map((day) => (
                                    <option key={day} value={day}>
                                        {day}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mealplan-detail-macro-chart-day-selector">
                            <label htmlFor="macro-chart-mode-selector">
                                Mode
                            </label>
                            <select
                                id="macro-chart-mode-selector"
                                value={chartMode}
                                onChange={(event) =>
                                    setChartMode(event.target.value)
                                }
                            >
                                <option value="discrete">Discrete</option>
                                <option value="cumulative">Cumulative</option>
                            </select>
                        </div>
                    </div>
                </div>

                {chartMealsForSelectedDay.length === 0 ? (
                    <div className="mealplan-detail-macro-chart-empty">
                        No meals planned for {selectedChartDay}.
                    </div>
                ) : (
                    <div className="mealplan-detail-macro-chart-body">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                )}
            </div>
        );
    };

    const getHeader = (title) => {
        return (
            <div className="mealplan-detail-header">
                <h1>
                    <EditLabel
                        value={title?.trim() || "No title."}
                        onChange={(newName) => {
                            if (newName && newName !== mealplan.name) {
                                const copy = { ...mealplan };
                                copy.name = newName;
                                updateResource({ name: newName });
                            }
                        }}
                        isEditable={isEditable}
                    />
                </h1>
            </div>
        );
    };

    const updateResource = async (newResource, fallback = () => {}) => {
        const token = await getAccessTokenSilently();
        const { data, status } = await updateMealPlan(token, {
            id: mealplan.id,
            ...newResource,
        });
        if (status === 200) {
            setMealPlan(data.mealplan);
            addNotification({
                message: "Meal plan updated successfully.",
            });
        } else {
            addNotification({
                message: "Meal plan update failed.",
            });
            setFlow("error");
            fallback();
        }
    };

    const onDescriptionChange = async (newDescription) => {
        if (newDescription && newDescription !== mealplan.description) {
            setDescription(newDescription);
            updateResource(
                {
                    description: newDescription,
                },
                () => setDescription(mealplan.description),
            );
        }
    };

    const getDescription = () => {
        return (
            <div className="mealplan-detail-description">
                <h4>Description</h4>

                <EditLabel
                    value={description?.trim() || "No description."}
                    placeholder="Description"
                    onChange={onDescriptionChange}
                    isEditable={isEditable}
                />
            </div>
        );
    };

    const onAddPlannedMealPrefs = async (newPlannedMealPrefs) => {
        const token = await getAccessTokenSilently();

        if (editingPlannedMealPrefId) {
            const deleteResponse = await deletePlannedMealPreference(
                token,
                selectedPreference.id,
                editingPlannedMealPrefId,
            );
            if (deleteResponse.status !== 200) {
                addNotification({ message: "Failed to update planned meal." });
                setEditingPlannedMealPrefId(null);
                setModalOpen(false);
                return;
            }
        }

        const response = await addPlannedMealPreferenceBatch(
            token,
            selectedPreference.id,
            newPlannedMealPrefs,
        );
        if (response.status === 200) {
            setSelectedPreference(response.data.mealplanpreference);
            instantiateMealPlanPreferences();
            addNotification({
                message: "Planned meal(s) added successfully.",
            });
        } else {
            console.error(response);
            setFlow("error");
            addNotification({
                message: "Planned meal(s) failed to be added.",
            });
        }
        setModalOpen(false);
        setEditingPlannedMealPrefId(null);
    };

    const onSaveProductMapping = async (productMapping) => {
        if (!productMapping) {
            console.error("productMapping was empty");
            return;
        }

        let productIds = [];
        for (const key in productMapping) {
            if (productMapping[key] !== null) {
                productIds = [productMapping[key]?.id, ...productIds];
            }
        }

        const token = await getAccessTokenSilently();

        // const payload = {
        //   id: selectedPreference?.id,
        //   product_mapping: {
        //     planned_products: productIds,
        //   },
        // };
        // const response = await updateMealPlanPreference(token, payload);

        const payload = {
            id: selectedPreference?.id,
            product_mapping: {
                planned_products: productIds,
            },
        };
        const response = await updateMealPlanPreference(token, payload);

        if (response.status === 200) {
            setProductModalOpen(false);
            addNotification({
                message: "Meal plan preferences updated successfully.",
            });
        } else {
            console.error(response);
            addNotification({
                message: "Meal plan update failed.",
            });
        }
    };

    const deleteResource = async () => {
        const token = await getAccessTokenSilently();
        const response = await deleteMealPlan(token, match.params.id);
        if (response.status === 200) {
            history.push("/mealplan");
            addNotification({
                message: "Meal plan deleted successfully.",
            });
        } else {
            console.error(response);
            setFlow("error");
            addNotification({
                message: "Meal plan deletion failed.",
            });
        }
    };

    const getFavoriteEmoji = () => {
        return currentMealplanId === mealplanId ? (
            <IoIcons.IoIosStar />
        ) : (
            <IoIcons.IoIosStarOutline />
        );
    };

    const setMealplanActive = async () => {
        if (mealplan.id === currentMealplanId) {
            return;
        }

        const token = await getAccessTokenSilently();
        const response = await setCurrentMealPlan(token, mealplanId);
        if (response.status === 200) {
            setCurrentMealPlanId(mealplanId);
        }
    };

    const getButtons = () => {
        return (
            <div className="mealplan-detail-peripheral-buttons">
                {isEditable ? (
                    <>
                        <div
                            className="mealplan-detail-create-plannedmeal-button"
                            onClick={() => { setModalPresets(null); setEditingPlannedMealPrefId(null); setModalOpen(true); }}
                        >
                            <FiIcons.FiPlus />
                        </div>
                        <div
                            className="mealplan-detail-create-plannedmeal-button"
                            onClick={() => setProductModalOpen(true)}
                        >
                            <FiIcons.FiLayers />
                        </div>
                    </>
                ) : null}

                <div
                    className="mealplan-detail-set-favorite-button"
                    onClick={() => setMealplanActive()}
                >
                    {getFavoriteEmoji()}
                </div>
                <div
                    className="mealplan-detail-shopping-list-button"
                    onClick={() =>
                        history.push(
                            `/mealplan/${mealplanId}/preference/${selectedPreference.id}/shoppinglist`,
                        )
                    }
                >
                    <FiIcons.FiShoppingBag />
                </div>
                {isEditable ? (
                    <div
                        className="mealplan-detail-delete-mealplan-button"
                        onClick={() => deleteResource()}
                    >
                        <FiIcons.FiTrash2 />
                    </div>
                ) : null}
            </div>
        );
    };

    const getCalendarView = () => {
        const plannedMealsByDay = daysOfTheWeek.reduce((accumulator, day) => {
            accumulator[day] =
                sortedPlannedMealPrefs?.filter(
                    (plannedMealPref) =>
                        plannedMealPref.day.toLowerCase() === day.toLowerCase(),
                ) || [];
            return accumulator;
        }, {});

        const mealsByDayAndSlot = daysOfTheWeek.reduce(
            (dayAccumulator, day) => {
                const mealsBySlot = Array.from({ length: 48 }, () => []);

                plannedMealsByDay[day].forEach((plannedMealPref) => {
                    const mealMinutes = getMealMinutesFromTime(
                        plannedMealPref?.time,
                    );

                    if (mealMinutes == null) {
                        return;
                    }

                    const slot = Math.floor(mealMinutes / 30);
                    if (slot >= 0 && slot <= 47) {
                        mealsBySlot[slot].push({
                            plannedMealPref,
                            mealMinutes,
                        });
                    }
                });

                mealsBySlot.forEach((slotMeals) => {
                    slotMeals.sort((a, b) => a.mealMinutes - b.mealMinutes);
                });

                dayAccumulator[day] = mealsBySlot;
                return dayAccumulator;
            },
            {},
        );

        const slotRows = Array.from({ length: 48 }, (_, slot) => slot);

        const mobileDayMeals = mealsByDayAndSlot[daysOfTheWeek[mobileDayIndex]];

        return (
            <div className="mealplan-detail-calendar-container">
                {/* Mobile day tabs */}
                <div className="mealplan-detail-mobile-day-tabs">
                    {daysOfTheWeek.map((day, i) => (
                        <button
                            key={day}
                            className={`mealplan-detail-mobile-day-tab${mobileDayIndex === i ? ' mealplan-detail-mobile-day-tab-active' : ''}`}
                            onClick={() => setMobileDayIndex(i)}
                        >
                            {day.substring(0, 3).toUpperCase()}
                        </button>
                    ))}
                </div>
                {/* Mobile single-day list */}
                <div className="mealplan-detail-mobile-day-view" ref={mobileScrollRef}>
                    <div
                        className={`mealplan-detail-mobile-nutrition-sticky${
                            showDailyNutrition
                                ? ""
                                : " mealplan-detail-mobile-nutrition-sticky-collapsed"
                        }`}
                    >
                        <NutritionSection
                            showNutrition={showDailyNutrition}
                            nutrition={getDailyNutrition(nutrition, daysOfTheWeek[mobileDayIndex])}
                        />
                        <button
                            type="button"
                            className="mealplan-detail-daily-nutrition-toggle"
                            onClick={() =>
                                setShowDailyNutrition((current) => !current)
                            }
                            aria-label={
                                showDailyNutrition
                                    ? "Collapse daily nutrition"
                                    : "Expand daily nutrition"
                            }
                        >
                            <span className="mealplan-detail-daily-nutrition-toggle-text">
                                {showDailyNutrition
                                    ? "Hide Daily Nutrition"
                                    : "Show Daily Nutrition"}
                            </span>
                            {showDailyNutrition ? (
                                <FiIcons.FiChevronUp />
                            ) : (
                                <FiIcons.FiChevronDown />
                            )}
                        </button>
                    </div>
                    {slotRows.map((slot) => {
                        const mealsForSlot = mobileDayMeals[slot];
                        const mobileDay = daysOfTheWeek[mobileDayIndex];
                        const hasMeals = mealsForSlot.length > 0;
                        const hasPending = pendingMoves.some(
                            (m) => m.day.toLowerCase() === mobileDay.toLowerCase() && m.slot === slot
                        );
                        const isFullHour = slot % 2 === 0;
                        // Show empty full-hour slots during drag so user can drop onto them
                        if (!hasMeals && !hasPending && !(isDragging && isFullHour)) return null;
                        return (
                            <div
                                ref={(el) => {
                                    mobileHourRefs.current[slot] = el;
                                }}
                                key={`mobile-slot-${slot}`}
                                data-drop-day={mobileDay}
                                data-drop-slot={slot}
                                className={`mealplan-detail-mobile-hour-block${dragOverCell === `mobile-${slot}` ? ' mealplan-detail-calendar-grid-hour-cell-dragover' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "move";
                                    setDragOverCell(`mobile-${slot}`);
                                }}
                                onDragLeave={() => {
                                    setDragOverCell((prev) => prev === `mobile-${slot}` ? null : prev);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOverCell(null);
                                    try {
                                        const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
                                        onMovePlannedMealPref(dragData, mobileDay, slot);
                                    } catch (err) {
                                        console.error("Drop parse error", err);
                                    }
                                }}
                            >
                                <div className="mealplan-detail-mobile-hour-label">{getTimelineTimeLabel(slot * 30)}</div>
                                {mealsForSlot.map(({ plannedMealPref }) => {
                                    const day = mobileDay;
                                    const lookupKey = buildMealLookupKey({
                                        day,
                                        mealPreferenceId: plannedMealPref?.mealpreference_id,
                                        time: plannedMealPref?.time,
                                    });
                                    return (
                                        <div
                                            key={`${day}-${plannedMealPref.mealpreference_id}-${plannedMealPref.time}`}
                                            data-meal-pref-id={plannedMealPref.id}
                                            onTouchStart={(e) => isEditable && handleTouchStart(plannedMealPref, e)}
                                        >
                                        <MealCard
                                            plannedMealPref={plannedMealPref}
                                            onDeletePlannedMealPref={onDeletePlannedMealPref}
                                            isEditable={isEditable}
                                            onCardDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                e.dataTransfer.dropEffect = "move";
                                            }}
                                            onCardDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setDragOverCell(null);
                                                try {
                                                    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
                                                    const sameSlot =
                                                        dragData.day.toLowerCase() === day.toLowerCase() &&
                                                        Math.floor(getMealMinutesFromTime(dragData.time) / 30) === slot;
                                                    if (sameSlot) {
                                                        onSwapPlannedMealPrefs(dragData, plannedMealPref);
                                                    } else {
                                                        onMovePlannedMealPref(dragData, day, slot);
                                                    }
                                                } catch (err) {
                                                    console.error("Drop parse error", err);
                                                }
                                            }}
                                            showNutrition={showNutrition}
                                            nutrition={
                                                mealNutritionByPlannedMealPrefId[plannedMealPref?.id] ||
                                                mealNutritionByLookupKey[lookupKey] ||
                                                mealNutritionByLookupKey[plannedMealPref?.mealpreference_id]
                                            }
                                            ingredientNutrition={
                                                ingredientNutritionByPlannedMealPrefId[plannedMealPref?.id] ||
                                                ingredientNutritionByLookupKey[lookupKey]
                                            }
                                            onEditPlannedMealPref={() => {
                                                const t = plannedMealPref.time || '';
                                                const timeForInput = t.length === 4
                                                    ? `${t.substring(0, 2)}:${t.substring(2, 4)}`
                                                    : t;
                                                setModalPresets({ day, time: timeForInput });
                                                setEditingPlannedMealPrefId(plannedMealPref.id);
                                                setModalOpen(true);
                                            }}
                                        />
                                        </div>
                                    );
                                })}
                                {pendingMoves
                                    .filter((m) => m.day.toLowerCase() === mobileDay.toLowerCase() && m.slot === slot)
                                    .map((m) => (
                                        <div key={m.id} className="mealplan-detail-moving-card">
                                            Moving meal…
                                        </div>
                                    ))
                                }
                                {isEditable && (
                                    <div
                                        className="mealplan-detail-calendar-ghost-card"
                                        style={{ display: 'flex' }}
                                        onClick={() => {
                                            const minutes = slot * 30;
                                            const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
                                            const mm = String(minutes % 60).padStart(2, '0');
                                            setModalPresets({ day: mobileDay, time: `${hh}:${mm}` });
                                            setEditingPlannedMealPrefId(null);
                                            setModalOpen(true);
                                        }}
                                    >
                                        <FiIcons.FiPlus />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {slotRows.every((slot) => mobileDayMeals[slot].length === 0) && (
                        <div className="mealplan-detail-mobile-empty">No meals planned</div>
                    )}
                </div>
                {/* Desktop grid */}
                <div className="mealplan-detail-calendar-scroll-wrapper" ref={desktopScrollRef}>
                <div
                    className="mealplan-detail-calendar-grid mealplan-detail-desktop-grid"
                    onDragStart={() => setIsDragging(true)}
                    onDragOver={(e) => handleEdgeAutoScroll(e.clientY, desktopScrollRef.current)}
                    onDragEnd={() => { setIsDragging(false); setDragOverCell(null); stopAutoScroll(); }}
                >
                    {daysOfTheWeek.map((day) => (
                        <div
                            key={`header-${day}`}
                            className="mealplan-detail-calendar-grid-cell mealplan-detail-calendar-grid-header"
                        >
                            {day.substring(0, 3).toUpperCase()}
                        </div>
                    ))}

                    {daysOfTheWeek.map((day) => (
                        <div
                            key={`nutrition-${day}`}
                            className={`mealplan-detail-calendar-grid-cell mealplan-detail-calendar-grid-nutrition-row${
                                showDailyNutrition
                                    ? ""
                                    : " mealplan-detail-calendar-grid-nutrition-row-collapsed"
                            }`}
                        >
                            <NutritionSection
                                showNutrition={showDailyNutrition}
                                nutrition={getDailyNutrition(nutrition, day)}
                            />
                        </div>
                    ))}

                    <div
                        className={`mealplan-detail-calendar-grid-cell mealplan-detail-daily-nutrition-toggle-row${
                            showDailyNutrition
                                ? ""
                                : " mealplan-detail-daily-nutrition-toggle-row-collapsed"
                        }`}
                    >
                        <button
                            type="button"
                            className="mealplan-detail-daily-nutrition-toggle"
                            onClick={() =>
                                setShowDailyNutrition((current) => !current)
                            }
                            aria-label={
                                showDailyNutrition
                                    ? "Collapse daily nutrition"
                                    : "Expand daily nutrition"
                            }
                        >
                            <span className="mealplan-detail-daily-nutrition-toggle-text">
                                {showDailyNutrition
                                    ? "Hide Daily Nutrition"
                                    : "Show Daily Nutrition"}
                            </span>
                            {showDailyNutrition ? (
                                <FiIcons.FiChevronUp />
                            ) : (
                                <FiIcons.FiChevronDown />
                            )}
                        </button>
                    </div>

                    {slotRows.map((slot) => {
                        const hour = Math.floor(slot / 2);
                        const isHalfHour = slot % 2 === 1;
                        const slotHasMeals = isHalfHour && daysOfTheWeek.some((d) => mealsByDayAndSlot[d][slot].length > 0);
                        return (
                        <React.Fragment key={`slot-row-${slot}`}>
                            {daysOfTheWeek.map((day) => {
                                const mealsForSlot =
                                    mealsByDayAndSlot[day][slot];
                                const isMonday = day === 'Monday';
                                return (
                                    <div
                                        ref={(el) => {
                                            if (isMonday) {
                                                desktopHourRefs.current[slot] = el;
                                            }
                                        }}
                                        key={`slot-${slot}-${day}`}
                                        className={`mealplan-detail-calendar-grid-cell mealplan-detail-calendar-grid-hour-cell${isHalfHour ? ' mealplan-detail-calendar-grid-half-hour-cell' : ''}${isMonday ? ' mealplan-detail-calendar-grid-hour-cell-first' : ''}${slot === 0 ? ' mealplan-detail-calendar-grid-hour-cell-midnight' : ''}${dragOverCell === `${slot}-${day}` ? ' mealplan-detail-calendar-grid-hour-cell-dragover' : ''}`}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                            setDragOverCell(`${slot}-${day}`);
                                        }}
                                        onDragLeave={() => {
                                            setDragOverCell((prev) => prev === `${slot}-${day}` ? null : prev);
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setDragOverCell(null);
                                            try {
                                                const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
                                                onMovePlannedMealPref(dragData, day, slot);
                                            } catch (err) {
                                                console.error("Drop parse error", err);
                                            }
                                        }}
                                    >
                                        {isMonday && (!isHalfHour || slotHasMeals) && (
                                            <span className="mealplan-detail-calendar-time-label">
                                                {getTimelineTimeLabel(slot * 30)}
                                            </span>
                                        )}
                                        <div className="mealplan-detail-calendar-hour-cell-content">
                                            {mealsForSlot.map(
                                                ({
                                                    plannedMealPref,
                                                    mealMinutes,
                                                }) => {
                                                    const lookupKey =
                                                        buildMealLookupKey({
                                                            day,
                                                            mealPreferenceId:
                                                                plannedMealPref?.mealpreference_id,
                                                            time: plannedMealPref?.time,
                                                        });

                                                    return (
                                                        <div
                                                            key={
                                                                `${day}-${plannedMealPref.mealpreference_id}-${plannedMealPref.time}`
                                                            }
                                                            className="mealplan-detail-calendar-hour-meal"
                                                            onDragOver={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                e.dataTransfer.dropEffect = "move";
                                                            }}
                                                            onDrop={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setDragOverCell(null);
                                                                try {
                                                                    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
                                                                    const sameSlot =
                                                                        dragData.day.toLowerCase() === day.toLowerCase() &&
                                                                        Math.floor(getMealMinutesFromTime(dragData.time) / 30) === slot;
                                                                    if (sameSlot) {
                                                                        onSwapPlannedMealPrefs(dragData, plannedMealPref);
                                                                    } else {
                                                                        onMovePlannedMealPref(dragData, day, slot);
                                                                    }
                                                                } catch (err) {
                                                                    console.error("Drop parse error", err);
                                                                }
                                                            }}
                                                        >
                                                            <MealCard
                                                                plannedMealPref={
                                                                    plannedMealPref
                                                                }
                                                                onDeletePlannedMealPref={
                                                                    onDeletePlannedMealPref
                                                                }
                                                                isEditable={
                                                                    isEditable
                                                                }
                                                                onCardDragOver={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    e.dataTransfer.dropEffect = "move";
                                                                }}
                                                                onCardDrop={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setDragOverCell(null);
                                                                    try {
                                                                        const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
                                                                        const sameSlot =
                                                                            dragData.day.toLowerCase() === day.toLowerCase() &&
                                                                            Math.floor(getMealMinutesFromTime(dragData.time) / 30) === slot;
                                                                        if (sameSlot) {
                                                                            onSwapPlannedMealPrefs(dragData, plannedMealPref);
                                                                        } else {
                                                                            onMovePlannedMealPref(dragData, day, slot);
                                                                        }
                                                                    } catch (err) {
                                                                        console.error("Drop parse error", err);
                                                                    }
                                                                }}
                                                                showNutrition={
                                                                    showNutrition
                                                                }
                                                                nutrition={
                                                                    mealNutritionByPlannedMealPrefId[
                                                                        plannedMealPref
                                                                            ?.id
                                                                    ] ||
                                                                    mealNutritionByLookupKey[
                                                                        lookupKey
                                                                    ] ||
                                                                    mealNutritionByLookupKey[
                                                                        plannedMealPref
                                                                            ?.mealpreference_id
                                                                    ]
                                                                }
                                                                ingredientNutrition={
                                                                    ingredientNutritionByPlannedMealPrefId[
                                                                        plannedMealPref
                                                                            ?.id
                                                                    ] ||
                                                                    ingredientNutritionByLookupKey[
                                                                        lookupKey
                                                                    ]
                                                                }
                                                                onEditPlannedMealPref={() => {
                                                                    const t = plannedMealPref.time || '';
                                                                    const timeForInput = t.length === 4
                                                                        ? `${t.substring(0, 2)}:${t.substring(2, 4)}`
                                                                        : t;
                                                                    setModalPresets({ day, time: timeForInput });
                                                                    setEditingPlannedMealPrefId(plannedMealPref.id);
                                                                    setModalOpen(true);
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                },
                                            )}
                                            {pendingMoves
                                                .filter((m) => m.day.toLowerCase() === day.toLowerCase() && m.slot === slot)
                                                .map((m) => (
                                                    <div key={m.id} className="mealplan-detail-moving-card">
                                                        Moving meal…
                                                    </div>
                                                ))
                                            }
                                            {isEditable && (
                                                <div
                                                    className="mealplan-detail-calendar-ghost-card"
                                                    onClick={() => {
                                                        const minutes = slot * 30;
                                                        const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
                                                        const mm = String(minutes % 60).padStart(2, '0');
                                                        setModalPresets({ day, time: `${hh}:${mm}` });
                                                        setEditingPlannedMealPrefId(null);
                                                        setModalOpen(true);
                                                    }}
                                                >
                                                    <FiIcons.FiPlus />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                        );
                    })}
                </div>
                </div>
            </div>
        );
    };

    const onCreateMealPlanPreference = () => {
        history.push(`/mealplan/${mealplan.id}/preference/create`);
    };

    const onEditMealPlanPreference = (preference) => {
        history.push(
            `/mealplan/${mealplan.id}/preference/${preference.id}/edit`,
        );
    };

    const onDeleteMealPlanPreference = async (preference) => {
        if (preference.name.toLowerCase() === "default") {
            return;
        }
        const token = await getAccessTokenSilently();
        const response = await deleteMealPlanPreference(token, preference.id);
        if (response.status === 200) {
            setSelectedPreference(
                preferences.filter((pref) => {
                    return pref.name.toLowerCase() === "default";
                })[0],
            );
            setMealPlanPreferences(
                mealPlanPreferences.filter((pref) => {
                    return pref.id !== preference.id;
                }),
            );
        }
    };

    return (
        <BackupView error={flow === "error"} loading={flow === "loading"}>
            <div className="mealplan-detail-container">
                {mealplan ? (
                    <div>
                        {getHeader(mealplan.name)}
                        <div className="mealplan-detail-content-container">
                            <div className="mealplan-detail-peripherals">
                                {getDescription()}
                                {getButtons()}
                            </div>
                            {/* <div style={{ margin: "0px 12px" }}>
                                <div
                                    style={{
                                        borderColor: "lightgrey",
                                        borderRadius: "3px",
                                        background: "lightgrey",
                                        color: "white",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-around",
                                        padding: "0px 5px",
                                        width: "fit-content",
                                    }}
                                    onClick={() => {
                                        setShowNutrition(!showNutrition);
                                    }}
                                >
                                    <div
                                        style={{
                                            marginTop: "2px",
                                        }}
                                    >
                                        {showNutrition ? (
                                            <FiIcons.FiChevronUp />
                                        ) : (
                                            <FiIcons.FiChevronDown />
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            margin: "0px 5px",
                                            minWidth: "95px",
                                        }}
                                    >
                                        {showNutrition ? "Hide " : "Show "}
                                        Nutrition
                                    </div>
                                </div>
                            </div> */}

                            {getCalendarView()}
                            {getMacroChart()}
                        </div>

                        <CreatePlannedMealModal
                            modalOpen={modalOpen}
                            setModalOpen={setModalOpen}
                            onAddPlannedMealPrefs={onAddPlannedMealPrefs}
                            presets={modalPresets}
                        />
                        <ManageProductsModal
                            modalOpen={productModalOpen}
                            setModalOpen={setProductModalOpen}
                            onSaveProductMapping={onSaveProductMapping}
                            mealPlanPrefId={selectedPreference?.id}
                            mealPlanProductMapping={
                                selectedPreference?.product_mapping
                            }
                        />
                    </div>
                ) : (
                    "Loading..."
                )}
            </div>
        </BackupView>
    );
};

export default MealPlanDetailPage;
